import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getContentHistory, type ContentHistoryEntry } from "@/lib/admin/content-history.functions";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { useAdmin } from "@/hooks/use-admin";
import {
  ArrowLeft,
  Save,
  Send,
  Trash2,
  Upload,
  Eye,
  Lock,
  Calendar,
  X,
  Image as ImageIcon,
  Sparkles,
  Plus,
  Check,
  FileText,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin/$id")({
  component: AdminEditor,
});

type Kind = "story" | "video" | "activity" | "challenge" | "coloring" | "worksheet" | "quiz" | "printable" | "parent_article";
type Tier = "free" | "family" | "premium";
type Status = "draft" | "scheduled" | "published" | "archived";
type Age = "4-6" | "7-9" | "10-12";

export type QuizChoice = {
  id: string;
  label_en: string;
  label_ar: string;
};

export type QuizQuestion = {
  id: string;
  prompt_en: string;
  prompt_ar: string;
  choices: QuizChoice[];
  correct_choice_id: string;
  points: number;
};

export type QuizData = {
  questions: QuizQuestion[];
  pass_score?: number;
};

type FormState = {
  kind: Kind;
  tier: Tier;
  status: Status;
  age_group: Age;
  title_en: string;
  title_ar: string;
  description_en: string;
  description_ar: string;
  body_en: string;
  body_ar: string;
  cover_url: string;
  media_url: string;
  embed_url: string;
  hls_url: string;
  subtitle_en_url: string;
  subtitle_ar_url: string;
  pdf_url: string;
  quiz_data: QuizData;
  allow_download: boolean;
  is_featured: boolean;
  tags: string[];
  value_tags: string[];
  character_ids: string[];
  scheduled_at: string;
  slug: string;
  reading_minutes: string;
};

const EMPTY_QUIZ: QuizData = { questions: [] };

const EMPTY: FormState = {
  kind: "story",
  tier: "free",
  status: "draft",
  age_group: "4-6",
  title_en: "",
  title_ar: "",
  description_en: "",
  description_ar: "",
  body_en: "",
  body_ar: "",
  cover_url: "",
  media_url: "",
  embed_url: "",
  hls_url: "",
  subtitle_en_url: "",
  subtitle_ar_url: "",
  pdf_url: "",
  quiz_data: EMPTY_QUIZ,
  allow_download: false,
  is_featured: false,
  tags: [],
  value_tags: [],
  character_ids: [],
  scheduled_at: "",
  slug: "",
  reading_minutes: "",
};



const KIND_OPTS: { value: Kind; en: string; ar: string; emoji: string }[] = [
  { value: "story", en: "Story", ar: "قصة", emoji: "📖" },
  { value: "video", en: "Video", ar: "فيديو", emoji: "🎬" },
  { value: "quiz", en: "Quiz", ar: "اختبار", emoji: "❓" },
  { value: "printable", en: "Printable", ar: "نشاط مطبوع", emoji: "🖨️" },
  { value: "parent_article", en: "Parent Article", ar: "مقال للأهل", emoji: "👨‍👩‍👧" },
  { value: "activity", en: "Activity", ar: "نشاط", emoji: "✨" },
  { value: "challenge", en: "Challenge", ar: "تحدي", emoji: "🎯" },
  { value: "coloring", en: "Coloring", ar: "تلوين", emoji: "🎨" },
  { value: "worksheet", en: "Worksheet", ar: "ورقة عمل", emoji: "📄" },
];


const TIER_OPTS: {
  value: Tier;
  en: string;
  ar: string;
  descEn: string;
  descAr: string;
  classes: string;
}[] = [
  {
    value: "free",
    en: "Free",
    ar: "مجاني",
    descEn: "Visible to everyone, no subscription needed.",
    descAr: "متاح للجميع بدون اشتراك.",
    classes: "bg-emerald-500/15 border-emerald-500 text-emerald-700 dark:text-emerald-300",
  },
  {
    value: "family",
    en: "Family",
    ar: "عائلة",
    descEn: "Family or Premium subscribers only.",
    descAr: "لمشتركي العائلة أو البريميوم فقط.",
    classes: "bg-amber-500/15 border-amber-500 text-amber-700 dark:text-amber-300",
  },
  {
    value: "premium",
    en: "Premium",
    ar: "بريميوم",
    descEn: "Premium subscribers only.",
    descAr: "لمشتركي البريميوم فقط.",
    classes: "bg-violet-500/15 border-violet-500 text-violet-700 dark:text-violet-300",
  },
];

const AGE_OPTS: Age[] = ["4-6", "7-9", "10-12"];

function AdminEditor() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const { isAdmin, loading: roleLoading } = useAdmin();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"cover" | "media" | "pdf" | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [stats, setStats] = useState<{ views: number; completions: number }>({
    views: 0,
    completions: 0,
  });
  const [valueOptions, setValueOptions] = useState<
    { code: string; label_ar: string; label_en: string; emoji: string }[]
  >([]);
  const [characterOptions, setCharacterOptions] = useState<
    { id: string; name_ar: string; name_en: string; color: string; avatar_url: string | null }[]
  >([]);

  useEffect(() => {
    (async () => {
      const [{ data: values }, { data: chars }] = await Promise.all([
        supabase
          .from("content_values")
          .select("code,label_ar,label_en,emoji")
          .eq("is_active", true)
          .order("sort_order"),
        supabase
          .from("characters")
          .select("id,name_ar,name_en,color,avatar_url")
          .eq("is_active", true)
          .order("sort_order"),
      ]);
      if (values) setValueOptions(values);
      if (chars) setCharacterOptions(chars);
    })();
  }, []);


  useEffect(() => {
    if (isNew || !isAdmin) return;
    (async () => {
      const [{ data }, { data: charLinks }] = await Promise.all([
        supabase.from("content_items").select("*").eq("id", id).single(),
        supabase.from("content_characters").select("character_id").eq("content_id", id),
      ]);
      if (data) {
        setForm({
          kind: data.kind,
          tier: data.tier,
          status: data.status,
          age_group: data.age_group,
          title_en: data.title_en ?? "",
          title_ar: data.title_ar ?? "",
          description_en: data.description_en ?? "",
          description_ar: data.description_ar ?? "",
          body_en: data.body_en ?? "",
          body_ar: data.body_ar ?? "",
          cover_url: data.cover_url ?? "",
          media_url: data.media_url ?? "",
          embed_url: data.embed_url ?? "",
          hls_url: data.hls_url ?? "",
          subtitle_en_url: data.subtitle_en_url ?? "",
          subtitle_ar_url: data.subtitle_ar_url ?? "",
          pdf_url: (data as { pdf_url?: string | null }).pdf_url ?? "",
          quiz_data:
            ((data as { quiz_data?: QuizData | null }).quiz_data as QuizData | null) ?? EMPTY_QUIZ,
          allow_download: data.allow_download ?? false,
          is_featured: (data as { is_featured?: boolean }).is_featured ?? false,
          tags: data.tags ?? [],
          value_tags: (data as { value_tags?: string[] }).value_tags ?? [],
          character_ids: (charLinks ?? []).map((l) => l.character_id),
          scheduled_at: data.scheduled_at
            ? new Date(data.scheduled_at).toISOString().slice(0, 16)
            : "",
          slug: (data as { slug?: string | null }).slug ?? "",
          reading_minutes:
            (data as { reading_minutes?: number | null }).reading_minutes != null
              ? String((data as { reading_minutes?: number | null }).reading_minutes)
              : "",
        });
        setStats({ views: data.views_count, completions: data.completions_count });
      }
      setLoading(false);
    })();
  }, [id, isNew, isAdmin]);

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function uploadFile(file: File, slot: "cover" | "media" | "pdf") {
    if (!user) return;
    setUploading(slot);
    const ext = file.name.split(".").pop() ?? "bin";
    const path = `${user.id}/${slot}/${Date.now()}.${ext}`;
    // Paid media + paid printables go to the private bucket. Covers always public.
    const useProtected = (slot === "media" || slot === "pdf") && form.tier !== "free";
    const bucket = useProtected ? "content-protected" : "content";
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { upsert: false, contentType: file.type });
    if (error) {
      alert(error.message);
      setUploading(null);
      return;
    }
    const value = useProtected
      ? `protected://${path}`
      : supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
    const target: keyof FormState =
      slot === "cover" ? "cover_url" : slot === "pdf" ? "pdf_url" : "media_url";
    update(target as "cover_url" | "media_url" | "pdf_url", value);
    setUploading(null);
  }



  async function save(publish?: boolean) {
    if (!form.title_en.trim() || !form.title_ar.trim()) {
      alert(
        isAr
          ? "العنوان مطلوب بالعربية والإنجليزية"
          : "Title is required in both Arabic and English",
      );
      return;
    }
    setSaving(true);
    const payload = {
      kind: form.kind,
      tier: form.tier,
      status: publish ? ("published" as Status) : form.status,
      age_group: form.age_group,
      title_en: form.title_en,
      title_ar: form.title_ar,
      description_en: form.description_en || null,
      description_ar: form.description_ar || null,
      body_en: form.body_en || null,
      body_ar: form.body_ar || null,
      cover_url: form.cover_url || null,
      media_url: form.media_url || null,
      embed_url: form.embed_url || null,
      hls_url: form.hls_url || null,
      subtitle_en_url: form.subtitle_en_url || null,
      subtitle_ar_url: form.subtitle_ar_url || null,
      pdf_url: form.kind === "printable" ? form.pdf_url || null : null,
      quiz_data: form.kind === "quiz" ? form.quiz_data : null,
      allow_download: form.allow_download,
      is_featured: form.is_featured,
      tags: form.tags,
      value_tags: form.value_tags,
      scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      published_at: publish ? new Date().toISOString() : undefined,
      created_by: user?.id,
      audience: form.kind === "parent_article" ? ("parent" as const) : ("child" as const),
      slug:
        form.kind === "parent_article"
          ? form.slug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "") ||
            null
          : null,
      reading_minutes:
        form.kind === "parent_article" && form.reading_minutes
          ? Math.max(1, Math.min(120, parseInt(form.reading_minutes, 10) || 0)) || null
          : null,
    };


    async function syncCharacters(contentId: string) {
      await supabase.from("content_characters").delete().eq("content_id", contentId);
      if (form.character_ids.length > 0) {
        await supabase.from("content_characters").insert(
          form.character_ids.map((character_id) => ({ content_id: contentId, character_id })),
        );
      }
    }

    if (isNew) {
      const { data, error } = await supabase
        .from("content_items")
        .insert(payload)
        .select("id")
        .single();
      if (error) {
        setSaving(false);
        return alert(error.message);
      }
      await syncCharacters(data.id);
      setSaving(false);
      navigate({ to: "/app/admin/$id", params: { id: data.id }, replace: true });
    } else {
      const { error } = await supabase
        .from("content_items")
        .update(payload)
        .eq("id", id);
      if (error) {
        setSaving(false);
        return alert(error.message);
      }
      await syncCharacters(id);
      setSaving(false);
      if (publish) update("status", "published");
    }
  }

  async function handleDelete() {
    if (isNew) return;
    if (!confirm(isAr ? "حذف نهائي؟" : "Delete permanently?")) return;
    const { error } = await supabase.from("content_items").delete().eq("id", id);
    if (error) return alert(error.message);
    navigate({ to: "/app/admin" });
  }

  if (roleLoading || loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div dir={dir} className="px-5 py-16 max-w-md mx-auto text-center">
        <Lock className="size-8 mx-auto mb-3 text-foreground/60" />
        <p className="text-foreground/60 mb-4">
          {isAr ? "للمسؤولين فقط" : "Admins only"}
        </p>
        <Link
          to="/app"
          className="inline-block px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm"
        >
          {isAr ? "العودة" : "Back"}
        </Link>
      </div>
    );
  }

  return (
    <div dir={dir} className="px-5 py-6 max-w-2xl mx-auto pb-32">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5 animate-fade-in">
        <Link
          to="/app/admin"
          className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground"
        >
          <ArrowLeft className="size-4 rtl:rotate-180" />
          {isAr ? "العودة" : "Back"}
        </Link>
        {!isNew && (
          <div className="flex gap-3 text-xs text-foreground/60">
            <span>👁 {stats.views}</span>
            <span>✓ {stats.completions}</span>
          </div>
        )}
      </div>

      <h1
        className={`${isAr ? "font-display-ar" : "font-display-en"} text-2xl font-bold mb-6 animate-fade-in`}
      >
        {isNew
          ? isAr
            ? "محتوى جديد"
            : "New content"
          : isAr
            ? "تحرير المحتوى"
            : "Edit content"}
      </h1>

      {/* Type */}
      <Section label={isAr ? "النوع" : "Type"}>
        <div className="grid grid-cols-3 gap-2">
          {KIND_OPTS.map((k) => (
            <button
              key={k.value}
              onClick={() => update("kind", k.value)}
              className={`p-3 rounded-2xl border-2 transition-all ${
                form.kind === k.value
                  ? "bg-primary/10 border-primary scale-105"
                  : "bg-card border-clay/30 hover:border-primary/50"
              }`}
            >
              <div className="text-2xl mb-1">{k.emoji}</div>
              <p className="text-[11px] font-bold">{isAr ? k.ar : k.en}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* Age + Tier */}
      <Section label={isAr ? "الفئة العمرية" : "Age group"}>
        <div className="flex gap-2">
          {AGE_OPTS.map((a) => (
            <button
              key={a}
              onClick={() => update("age_group", a)}
              className={`flex-1 py-2.5 rounded-2xl border-2 font-bold text-sm transition-all ${
                form.age_group === a
                  ? "bg-secondary text-secondary-foreground border-secondary"
                  : "bg-card border-clay/30"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </Section>

      <Section label={isAr ? "الوصول" : "Access tier"}>
        <div className="grid gap-2">
          {TIER_OPTS.map((t) => {
            const active = form.tier === t.value;
            return (
              <button
                key={t.value}
                onClick={() => update("tier", t.value)}
                className={`text-start p-3 rounded-2xl border-2 transition-all ${
                  active ? t.classes : "bg-card border-clay/30 hover:border-foreground/30"
                }`}
              >
                <div className="font-bold text-sm">{isAr ? t.ar : t.en}</div>
                <div className="text-[11px] opacity-80 mt-0.5">
                  {isAr ? t.descAr : t.descEn}
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      <Section label={isAr ? "مميّز على الرئيسية" : "Feature on home"}>
        <button
          type="button"
          onClick={() => update("is_featured", !form.is_featured)}
          className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${
            form.is_featured
              ? "bg-amber-500/15 border-amber-500 text-amber-700 dark:text-amber-300"
              : "bg-card border-clay/30 hover:border-amber-500/60"
          }`}
        >
          <span
            className={`size-9 rounded-xl flex items-center justify-center ${
              form.is_featured ? "bg-amber-500 text-white" : "bg-clay/40 text-foreground/60"
            }`}
          >
            <Sparkles className="size-4" />
          </span>
          <span className="flex-1 text-start">
            <span className="block font-bold text-sm">
              {form.is_featured
                ? isAr ? "مميّز ✓" : "Featured ✓"
                : isAr ? "غير مميّز" : "Not featured"}
            </span>
            <span className="block text-[11px] opacity-80">
              {isAr
                ? "يظهر في قسم \"المميّز\" على الصفحة الرئيسية للأطفال."
                : "Appears in the Featured shelf on the kids home."}
            </span>
          </span>
        </button>
      </Section>


      {/* Bilingual titles */}
      <Section label={isAr ? "العنوان" : "Title"}>
        <Field
          label="English"
          value={form.title_en}
          onChange={(v) => update("title_en", v)}
          placeholder="The Lion and the Mouse"
        />
        <Field
          label="العربية"
          value={form.title_ar}
          onChange={(v) => update("title_ar", v)}
          placeholder="الأسد والفأر"
          rtl
        />
      </Section>

      {/* Descriptions */}
      <Section label={isAr ? "وصف قصير" : "Short description"}>
        <Field
          label="English"
          value={form.description_en}
          onChange={(v) => update("description_en", v)}
          multiline
        />
        <Field
          label="العربية"
          value={form.description_ar}
          onChange={(v) => update("description_ar", v)}
          multiline
          rtl
        />
      </Section>

      {/* Body */}
      <Section label={isAr ? "المحتوى الكامل" : "Full content"}>
        <Field
          label="English"
          value={form.body_en}
          onChange={(v) => update("body_en", v)}
          multiline
          rows={6}
        />
        <Field
          label="العربية"
          value={form.body_ar}
          onChange={(v) => update("body_ar", v)}
          multiline
          rows={6}
          rtl
        />
      </Section>

      {/* Media */}
      <Section label={isAr ? "صورة الغلاف" : "Cover image"}>
        <MediaSlot
          url={form.cover_url}
          onUpload={(f) => uploadFile(f, "cover")}
          onClear={() => update("cover_url", "")}
          uploading={uploading === "cover"}
          isAr={isAr}
          accept="image/*"
          kind="image"
        />
      </Section>

      <Section label={isAr ? "ملف الوسائط (اختياري)" : "Media file (optional)"}>
        <MediaSlot
          url={form.media_url}
          onUpload={(f) => uploadFile(f, "media")}
          onClear={() => update("media_url", "")}
          uploading={uploading === "media"}
          isAr={isAr}
          accept="image/*,video/*,application/pdf"
          kind="file"
        />
        <input
          type="url"
          placeholder={isAr ? "أو ضع رابط فيديو (YouTube...)" : "Or paste an embed URL (YouTube...)"}
          value={form.embed_url}
          onChange={(e) => update("embed_url", e.target.value)}
          className="mt-2 w-full px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
        />
        <input
          type="url"
          placeholder={isAr ? "رابط بث HLS (m3u8) — اختياري" : "HLS stream URL (m3u8) — optional"}
          value={form.hls_url}
          onChange={(e) => update("hls_url", e.target.value)}
          className="mt-2 w-full px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
        />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input
            type="url"
            placeholder={isAr ? "ترجمة إنجليزية (.vtt)" : "EN subtitles (.vtt)"}
            value={form.subtitle_en_url}
            onChange={(e) => update("subtitle_en_url", e.target.value)}
            className="px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />
          <input
            type="url"
            placeholder={isAr ? "ترجمة عربية (.vtt)" : "AR subtitles (.vtt)"}
            value={form.subtitle_ar_url}
            onChange={(e) => update("subtitle_ar_url", e.target.value)}
            className="px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.allow_download}
            onChange={(e) => update("allow_download", e.target.checked)}
            className="h-4 w-4"
          />
          {isAr ? "السماح بالتنزيل (بريميوم دون اتصال)" : "Allow download (premium offline)"}
        </label>
      </Section>

      {/* Printable PDF (only for kind=printable) */}
      {form.kind === "printable" && (
        <Section label={isAr ? "ملف PDF القابل للطباعة" : "Printable PDF file"}>
          <p className="text-[11px] text-foreground/60 mb-2">
            {isAr
              ? form.tier === "free"
                ? "ملف PDF عام يصل إليه الجميع. يُحفظ في المخزن العام."
                : "ملف PDF محمي — يصل إليه المشتركون فقط عبر رابط موقّت."
              : form.tier === "free"
                ? "Public PDF anyone can download. Stored in the public bucket."
                : "Protected PDF — only entitled subscribers can open via a short-lived signed URL."}
          </p>
          <MediaSlot
            url={form.pdf_url}
            onUpload={(f) => uploadFile(f, "pdf")}
            onClear={() => update("pdf_url", "")}
            uploading={uploading === "pdf"}
            isAr={isAr}
            accept="application/pdf"
            kind="file"
          />
          {form.pdf_url && form.pdf_url.startsWith("protected://") && (
            <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <Lock className="size-3" />
              {isAr ? "محمي — رابط موقّت عند العرض" : "Protected — signed URL on view"}
            </p>
          )}
        </Section>
      )}

      {/* Parent article meta (only for kind=parent_article) */}
      {form.kind === "parent_article" && (
        <Section label={isAr ? "إعدادات مقال الأهل" : "Parent article settings"}>
          <p className="text-[11px] text-foreground/60 mb-2">
            {isAr
              ? "هذا المقال موجّه للأهل، ويظهر في قسم /parents وفي نتائج البحث. اختر رابطًا واضحًا بالإنجليزية."
              : "This article is for parents — it appears under /parents and in search results. Pick a clear English slug."}
          </p>
          <label className="block text-xs font-bold text-foreground/70 mb-1">
            {isAr ? "الرابط (slug)" : "URL slug"}
          </label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => update("slug", e.target.value)}
            placeholder="how-to-raise-honest-kids"
            className="w-full px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm font-mono"
            dir="ltr"
          />
          {form.slug && (
            <p className="mt-1 text-[11px] text-foreground/50" dir="ltr">
              /parents/{form.slug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "")}
            </p>
          )}
          <label className="block text-xs font-bold text-foreground/70 mt-3 mb-1">
            {isAr ? "وقت القراءة (دقائق)" : "Reading minutes"}
          </label>
          <input
            type="number"
            min={1}
            max={120}
            value={form.reading_minutes}
            onChange={(e) => update("reading_minutes", e.target.value)}
            placeholder="5"
            className="w-32 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />
        </Section>
      )}

      {/* Quiz builder (only for kind=quiz) */}
      {form.kind === "quiz" && (
        <Section label={isAr ? "أسئلة الاختبار" : "Quiz questions"}>
          <QuizBuilder
            value={form.quiz_data}
            onChange={(v) => update("quiz_data", v)}
            isAr={isAr}
          />
        </Section>
      )}


      {/* Tags */}
      <Section label={isAr ? "الوسوم" : "Tags"}>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {form.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/15 text-primary text-xs font-bold"
            >
              {t}
              <button
                onClick={() => update("tags", form.tags.filter((x) => x !== t))}
                className="hover:opacity-70"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tagInput.trim()) {
                e.preventDefault();
                if (!form.tags.includes(tagInput.trim())) {
                  update("tags", [...form.tags, tagInput.trim()]);
                }
                setTagInput("");
              }
            }}
            placeholder={isAr ? "أضف وسمًا واضغط Enter" : "Add tag and press Enter"}
            className="flex-1 px-3 py-2 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />
        </div>
      </Section>

      {/* Value tags (curriculum taxonomy) */}
      <Section label={isAr ? "القيم التربوية" : "Educational values"}>
        <p className="text-[11px] text-foreground/60 mb-2">
          {isAr
            ? "اختر القيم التي يعالجها هذا المحتوى — تُستخدم في التصفية والتوصيات وصفحات SEO."
            : "Pick the values this content nurtures — used for filtering, recommendations and SEO pages."}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {valueOptions.map((v) => {
            const active = form.value_tags.includes(v.code);
            return (
              <button
                key={v.code}
                type="button"
                onClick={() =>
                  update(
                    "value_tags",
                    active
                      ? form.value_tags.filter((x) => x !== v.code)
                      : [...form.value_tags, v.code],
                  )
                }
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all ${
                  active
                    ? "bg-accent/20 border-accent text-accent-foreground"
                    : "bg-card border-clay/30 hover:border-accent/60"
                }`}
              >
                <span>{v.emoji}</span>
                <span>{isAr ? v.label_ar : v.label_en}</span>
              </button>
            );
          })}
          {valueOptions.length === 0 && (
            <p className="text-xs text-foreground/50">
              {isAr ? "لا توجد قيم بعد." : "No values defined yet."}
            </p>
          )}
        </div>
      </Section>

      {/* Characters */}
      <Section label={isAr ? "الشخصيات" : "Characters"}>
        <p className="text-[11px] text-foreground/60 mb-2">
          {isAr
            ? "اختر الشخصيات التي تظهر في هذا المحتوى — تربط القصص بصفحات الشخصيات."
            : "Pick the characters that appear in this content — links it to character pages."}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {characterOptions.map((c) => {
            const active = form.character_ids.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() =>
                  update(
                    "character_ids",
                    active
                      ? form.character_ids.filter((x) => x !== c.id)
                      : [...form.character_ids, c.id],
                  )
                }
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all ${
                  active ? "text-white" : "bg-card border-clay/30 hover:border-primary/60"
                }`}
                style={
                  active
                    ? { background: c.color, borderColor: c.color }
                    : undefined
                }
              >
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt="" className="size-4 rounded-full object-cover" />
                ) : (
                  <span
                    className="size-4 rounded-full"
                    style={{ background: c.color }}
                    aria-hidden
                  />
                )}
                <span>{isAr ? c.name_ar : c.name_en}</span>
              </button>
            );
          })}
          {characterOptions.length === 0 && (
            <p className="text-xs text-foreground/50">
              {isAr ? "لا توجد شخصيات بعد." : "No characters defined yet."}
            </p>
          )}
        </div>
      </Section>





      {/* Schedule */}
      <Section label={isAr ? "النشر المجدول (اختياري)" : "Schedule (optional)"}>
        <div className="relative">
          <Calendar className="size-4 absolute top-1/2 -translate-y-1/2 start-3 text-foreground/40" />
          <input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => {
              update("scheduled_at", e.target.value);
              if (e.target.value && form.status === "draft") update("status", "scheduled");
            }}
            className="w-full ps-10 pe-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />
        </div>
      </Section>

      {/* Status */}
      <Section label={isAr ? "الحالة" : "Status"}>
        <div className="grid grid-cols-4 gap-2">
          {(["draft", "scheduled", "published", "archived"] as Status[]).map((s) => (
            <button
              key={s}
              onClick={() => update("status", s)}
              className={`py-2 rounded-2xl border-2 text-[11px] font-bold capitalize transition-all ${
                form.status === s
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card border-clay/30"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Section>

      {/* Preview */}
      <button
        onClick={() => setShowPreview(true)}
        className="w-full mt-2 mb-6 inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-card border-2 border-clay/30 hover:border-primary text-sm font-bold transition-colors"
      >
        <Eye className="size-4" />
        {isAr ? "معاينة" : "Preview"}
      </button>

      {/* History */}
      {!isNew && (
        <Section label={isAr ? "سجل التعديلات" : "Edit history"}>
          <HistoryPanel contentId={id} isAr={isAr} />
        </Section>
      )}


      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-card/95 backdrop-blur border-t-2 border-clay/30 px-5 py-3">
        <div className="max-w-2xl mx-auto flex gap-2">
          {!isNew && (
            <button
              onClick={handleDelete}
              className="size-11 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20 transition-colors"
              aria-label="Delete"
            >
              <Trash2 className="size-4" />
            </button>
          )}
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-card border-2 border-clay/40 font-bold text-sm hover:border-primary transition-colors disabled:opacity-50"
          >
            <Save className="size-4" />
            {saving ? "..." : isAr ? "حفظ" : "Save"}
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm shadow-lg disabled:opacity-50"
          >
            <Send className="size-4" />
            {isAr ? "نشر" : "Publish"}
          </button>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div
          onClick={() => setShowPreview(false)}
          className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur flex items-end sm:items-center justify-center p-4 animate-fade-in"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            dir={dir}
            className="bg-background rounded-3xl max-w-md w-full max-h-[85vh] overflow-y-auto animate-scale-in"
          >
            {form.cover_url && (
              <img
                src={form.cover_url}
                alt=""
                className="w-full aspect-video object-cover rounded-t-3xl"
              />
            )}
            <div className="p-5">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  {KIND_OPTS.find((k) => k.value === form.kind)?.emoji}{" "}
                  {isAr
                    ? KIND_OPTS.find((k) => k.value === form.kind)?.ar
                    : KIND_OPTS.find((k) => k.value === form.kind)?.en}
                </span>
                <span className="text-[10px] text-foreground/50 font-bold">
                  · {form.age_group} · {form.tier}
                </span>
              </div>
              <h2
                className={`${isAr ? "font-display-ar" : "font-display-en"} text-2xl font-bold mb-2`}
              >
                {isAr ? form.title_ar || form.title_en : form.title_en || form.title_ar}
              </h2>
              <p className="text-sm text-foreground/70 mb-4">
                {isAr
                  ? form.description_ar || form.description_en
                  : form.description_en || form.description_ar}
              </p>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {isAr ? form.body_ar || form.body_en : form.body_en || form.body_ar}
              </div>
              {form.embed_url && (
                <a
                  href={form.embed_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block mt-4 text-xs text-primary underline truncate"
                >
                  {form.embed_url}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <p className="text-xs font-bold uppercase tracking-widest text-foreground/60 mb-2">
        {label}
      </p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

const ACTION_META: Record<string, { en: string; ar: string; emoji: string }> = {
  "content.create": { en: "Created", ar: "أنشأ", emoji: "✨" },
  "content.update": { en: "Edited", ar: "عدّل", emoji: "✏️" },
  "content.publish": { en: "Published", ar: "نشر", emoji: "🚀" },
  "content.archive": { en: "Archived", ar: "أرشف", emoji: "📦" },
  "content.delete": { en: "Deleted", ar: "حذف", emoji: "🗑️" },
};

function relativeTime(iso: string, isAr: boolean): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return isAr ? "الآن" : "just now";
  if (m < 60) return isAr ? `قبل ${m} د` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return isAr ? `قبل ${h} س` : `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return isAr ? `قبل ${d} ي` : `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function HistoryPanel({ contentId, isAr }: { contentId: string; isAr: boolean }) {
  const fetchHistory = useServerFn(getContentHistory);
  const [rows, setRows] = useState<ContentHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchHistory({ data: { content_id: contentId, limit: 50 } });
        if (alive) setRows(res);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [contentId, fetchHistory]);

  if (error) {
    return <p className="text-xs text-foreground/50">{error}</p>;
  }
  if (rows === null) {
    return <p className="text-xs text-foreground/50">{isAr ? "جاري التحميل..." : "Loading..."}</p>;
  }
  if (rows.length === 0) {
    return <p className="text-xs text-foreground/50">{isAr ? "لا يوجد سجل بعد" : "No history yet."}</p>;
  }
  return (
    <div className="bg-card border-2 border-clay/30 rounded-2xl divide-y divide-clay/20 max-h-72 overflow-y-auto">
      {rows.map((r) => {
        const meta = ACTION_META[r.action] ?? { en: r.action, ar: r.action, emoji: "•" };
        return (
          <div key={r.id} className="p-3 flex items-start justify-between gap-3 text-xs">
            <div className="min-w-0 flex-1">
              <p className="font-bold flex items-center gap-1.5">
                <span>{meta.emoji}</span>
                <span>{isAr ? meta.ar : meta.en}</span>
              </p>
              <p className="text-foreground/60 font-mono truncate mt-0.5">
                {r.actor_email ?? "—"}
              </p>
            </div>
            <span
              className="text-foreground/50 shrink-0 whitespace-nowrap"
              title={new Date(r.created_at).toLocaleString()}
            >
              {relativeTime(r.created_at, isAr)}
            </span>
          </div>
        );
      })}
    </div>
  );
}


function Field({
  label,
  value,
  onChange,
  placeholder,
  multiline,
  rows = 3,
  rtl,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  rtl?: boolean;
}) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          dir={rtl ? "rtl" : "ltr"}
          className="w-full mt-1 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm resize-y"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          dir={rtl ? "rtl" : "ltr"}
          className="w-full mt-1 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
        />
      )}
    </div>
  );
}

function MediaSlot({
  url,
  onUpload,
  onClear,
  uploading,
  isAr,
  accept,
  kind,
}: {
  url: string;
  onUpload: (f: File) => void;
  onClear: () => void;
  uploading: boolean;
  isAr: boolean;
  accept: string;
  kind: "image" | "file";
}) {
  return (
    <div className="relative bg-card border-2 border-dashed border-clay/40 rounded-2xl overflow-hidden">
      {url ? (
        <div className="relative">
          {kind === "image" ? (
            <img src={url} alt="" className="w-full aspect-video object-cover" />
          ) : (
            <div className="p-4 flex items-center gap-3">
              <ImageIcon className="size-5 text-primary" />
              <span className="text-xs truncate flex-1">{url.split("/").pop()}</span>
            </div>
          )}
          <button
            onClick={onClear}
            className="absolute top-2 end-2 size-7 rounded-full bg-background/90 flex items-center justify-center hover:bg-background"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center py-8 cursor-pointer hover:bg-clay/10 transition-colors">
          <input
            type="file"
            accept={accept}
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
            }}
          />
          {uploading ? (
            <div className="size-6 rounded-full border-2 border-clay border-t-primary animate-spin" />
          ) : (
            <>
              <Upload className="size-5 text-foreground/40 mb-1.5" />
              <span className="text-xs font-bold text-foreground/60">
                {isAr ? "اختر ملفًا للرفع" : "Tap to upload"}
              </span>
            </>
          )}
        </label>
      )}
    </div>
  );
}

function newChoiceId() {
  return `c_${Math.random().toString(36).slice(2, 9)}`;
}
function newQuestionId() {
  return `q_${Math.random().toString(36).slice(2, 9)}`;
}

function QuizBuilder({
  value,
  onChange,
  isAr,
}: {
  value: QuizData;
  onChange: (v: QuizData) => void;
  isAr: boolean;
}) {
  const questions = value.questions ?? [];

  function setQuestions(next: QuizQuestion[]) {
    onChange({ ...value, questions: next });
  }
  function addQuestion() {
    const c1 = newChoiceId();
    const c2 = newChoiceId();
    const q: QuizQuestion = {
      id: newQuestionId(),
      prompt_en: "",
      prompt_ar: "",
      choices: [
        { id: c1, label_en: "", label_ar: "" },
        { id: c2, label_en: "", label_ar: "" },
      ],
      correct_choice_id: c1,
      points: 10,
    };
    setQuestions([...questions, q]);
  }
  function updateQ(idx: number, patch: Partial<QuizQuestion>) {
    setQuestions(questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }
  function removeQ(idx: number) {
    setQuestions(questions.filter((_, i) => i !== idx));
  }
  function addChoice(qi: number) {
    const id = newChoiceId();
    updateQ(qi, {
      choices: [...questions[qi].choices, { id, label_en: "", label_ar: "" }],
    });
  }
  function updateChoice(qi: number, ci: number, patch: Partial<QuizChoice>) {
    updateQ(qi, {
      choices: questions[qi].choices.map((c, i) => (i === ci ? { ...c, ...patch } : c)),
    });
  }
  function removeChoice(qi: number, ci: number) {
    const q = questions[qi];
    if (q.choices.length <= 2) return;
    const newChoices = q.choices.filter((_, i) => i !== ci);
    const correct =
      q.correct_choice_id === q.choices[ci].id ? newChoices[0].id : q.correct_choice_id;
    updateQ(qi, { choices: newChoices, correct_choice_id: correct });
  }

  return (
    <div className="space-y-3">
      {questions.length === 0 && (
        <p className="text-xs text-foreground/50">
          {isAr ? "لا أسئلة بعد. أضف أول سؤال أدناه." : "No questions yet. Add the first one below."}
        </p>
      )}
      {questions.map((q, qi) => (
        <div
          key={q.id}
          className="rounded-2xl border-2 border-clay/30 bg-card p-3 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-widest text-foreground/60">
              {isAr ? `سؤال ${qi + 1}` : `Question ${qi + 1}`}
            </span>
            <button
              type="button"
              onClick={() => removeQ(qi)}
              className="text-destructive hover:opacity-70"
              aria-label="remove"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
          <input
            value={q.prompt_en}
            onChange={(e) => updateQ(qi, { prompt_en: e.target.value })}
            placeholder="Question (English)"
            className="w-full px-3 py-2 rounded-xl bg-background border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />
          <input
            value={q.prompt_ar}
            onChange={(e) => updateQ(qi, { prompt_ar: e.target.value })}
            placeholder="السؤال بالعربية"
            dir="rtl"
            className="w-full px-3 py-2 rounded-xl bg-background border-2 border-clay/30 focus:border-primary outline-none text-sm"
          />

          <div className="space-y-1.5">
            {q.choices.map((c, ci) => {
              const isCorrect = q.correct_choice_id === c.id;
              return (
                <div
                  key={c.id}
                  className={`flex items-center gap-2 p-2 rounded-xl border-2 ${
                    isCorrect ? "border-emerald-500 bg-emerald-500/10" : "border-clay/30"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => updateQ(qi, { correct_choice_id: c.id })}
                    className={`size-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      isCorrect
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-clay/50"
                    }`}
                    aria-label="mark correct"
                  >
                    {isCorrect && <Check className="size-3.5" />}
                  </button>
                  <div className="flex-1 grid grid-cols-2 gap-1.5">
                    <input
                      value={c.label_en}
                      onChange={(e) => updateChoice(qi, ci, { label_en: e.target.value })}
                      placeholder="EN"
                      className="px-2 py-1.5 rounded-lg bg-background border border-clay/30 focus:border-primary outline-none text-xs"
                    />
                    <input
                      value={c.label_ar}
                      onChange={(e) => updateChoice(qi, ci, { label_ar: e.target.value })}
                      placeholder="عربي"
                      dir="rtl"
                      className="px-2 py-1.5 rounded-lg bg-background border border-clay/30 focus:border-primary outline-none text-xs"
                    />
                  </div>
                  {q.choices.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeChoice(qi, ci)}
                      className="size-6 rounded-full text-foreground/50 hover:text-destructive shrink-0"
                      aria-label="remove choice"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => addChoice(qi)}
              className="w-full py-1.5 rounded-xl border-2 border-dashed border-clay/40 text-[11px] font-bold text-foreground/60 hover:border-primary hover:text-primary inline-flex items-center justify-center gap-1"
            >
              <Plus className="size-3" />
              {isAr ? "أضف خيارًا" : "Add choice"}
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="text-foreground/60">{isAr ? "نقاط" : "Points"}:</span>
            <input
              type="number"
              min={1}
              max={1000}
              value={q.points}
              onChange={(e) => updateQ(qi, { points: Math.max(1, Number(e.target.value) || 1) })}
              className="w-20 px-2 py-1 rounded-lg bg-background border border-clay/30 focus:border-primary outline-none"
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addQuestion}
        className="w-full py-2.5 rounded-2xl border-2 border-dashed border-clay/40 font-bold text-sm hover:border-primary hover:text-primary inline-flex items-center justify-center gap-1.5"
      >
        <Plus className="size-4" />
        {isAr ? "أضف سؤالًا" : "Add question"}
      </button>
      {questions.length > 0 && (
        <div className="flex items-center gap-2 text-xs pt-2 border-t border-clay/20">
          <span className="text-foreground/60">{isAr ? "نسبة النجاح %" : "Pass score %"}:</span>
          <input
            type="number"
            min={0}
            max={100}
            value={value.pass_score ?? 60}
            onChange={(e) =>
              onChange({
                ...value,
                pass_score: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
              })
            }
            className="w-20 px-2 py-1 rounded-lg bg-background border border-clay/30 focus:border-primary outline-none"
          />
          <span className="text-foreground/40">·</span>
          <span className="text-foreground/60 inline-flex items-center gap-1">
            <FileText className="size-3" /> {questions.length}{" "}
            {isAr ? "أسئلة" : "questions"}
          </span>
        </div>
      )}
    </div>
  );
}

